import { styled } from '@mui/material/styles';
import { SIDEBAR_TRANSITION_OPTIONS } from '@openx/constants/styles.ts';
import type { NavNestedRouteConfig, RouterMethod } from '@openx/utils/routes/navTypes.ts';
import { SIZES } from '../theme/sizes.ts';
import { MenuItem } from './MenuItem.tsx';

interface SideNavProps {
  routes: NavNestedRouteConfig[];
  activeIndicatorEnabled?: boolean;
  onNavItemClick?: (path: string, method: RouterMethod) => void;
  isOpen?: boolean;
}

const StyledContainerDiv = styled('div')<{ isOpen: boolean }>`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-right-color: ${({ theme }) => theme.palette.divider};
  border-right-style: solid;
  border-right-width: 1px;
  overflow-y: auto;
  padding-bottom: ${({ theme }) => theme.spacing(3)};
  min-width: ${SIZES.sidebarWidth}px;
  width: ${SIZES.sidebarWidth}px;
  grid-row: 2;
  grid-column: 1;
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: ${({ theme }) => theme.transitions.create('visibility', SIDEBAR_TRANSITION_OPTIONS)};
`;

const StyledMenuSectionDiv = styled('div')`
  font-weight: 600;
  padding: ${({ theme }) => theme.spacing(3, 0, 0, 0)};
`;

const StyledMenuHeaderDiv = styled('div')`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 3px;
  line-height: 18px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  text-transform: uppercase;
  color: ${({ theme }) => theme.palette.primary.main};
  padding-left: ${({ theme }) => theme.spacing(4)};
`;

/**
 * @deprecated - Please use the [SideNav (v2)](./SideNav.v2.tsx) instead.
 */
export function SideNav({ routes, activeIndicatorEnabled, onNavItemClick, isOpen = true }: SideNavProps) {
  return (
    <StyledContainerDiv isOpen={isOpen}>
      {routes.map(({ title, routes }, index) => {
        const permittedRoutes = routes.filter(({ permissionCheck }) => permissionCheck());

        if (permittedRoutes.length === 0) {
          return null;
        }

        return (
          <StyledMenuSectionDiv key={index} data-test="menu-section">
            <StyledMenuHeaderDiv data-test="section-title">{title}</StyledMenuHeaderDiv>

            {permittedRoutes.map(route => (
              <MenuItem
                activeIndicatorEnabled={activeIndicatorEnabled}
                activePathOverride={route.activePathOverride}
                onClick={onNavItemClick}
                path={route.path}
                method={route.method}
                title={route.title}
                key={route.path}
                Icon={route.Icon}
              />
            ))}
          </StyledMenuSectionDiv>
        );
      })}
    </StyledContainerDiv>
  );
}
